/**
 * Versioned client-side persistence. Every persisted entry is wrapped in a `{ version, data }`
 * envelope and shape-checked on read, so a stale payload, a hand-edited entry, or a value another
 * app left on a shared host is discarded rather than parsed as current state.
 *
 * Bumping STORAGE_VERSION invalidates every persisted entry at once — that is the global kill
 * switch. There is deliberately no migration path: persisted state here is UI preference, cheap to
 * rebuild, and a migration is a second shape to keep correct forever.
 *
 * `encodeVersioned`/`decodeVersioned` are the envelope itself, separated from localStorage so the
 * `DEFAULT_DATA` session cookie rides the same version number and one bump clears both.
 */

export const STORAGE_VERSION = 1;

type VersionedBlob = {
	version: number;
	data: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isVersionedBlob = (value: unknown): value is VersionedBlob =>
	isRecord(value) && value.version === STORAGE_VERSION && 'data' in value;

export function encodeVersioned<T>({ data }: { data: T }): string {
	return JSON.stringify({ version: STORAGE_VERSION, data });
}

export type DecodeVersionedInput<T> = {
	raw: string | null | undefined;
	/** Rejects a payload whose shape no longer matches, so callers never cast blindly. */
	isValid: (value: unknown) => value is T;
};

export function decodeVersioned<T>({ raw, isValid }: DecodeVersionedInput<T>): T | null {
	if (!raw) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!isVersionedBlob(parsed) || !isValid(parsed.data)) return null;
		return parsed.data;
	} catch {
		return null;
	}
}

export type ReadVersionedInput<T> = {
	key: string;
	isValid: (value: unknown) => value is T;
};

export function readVersioned<T>({ key, isValid }: ReadVersionedInput<T>): T | null {
	try {
		return decodeVersioned({ raw: localStorage.getItem(key), isValid });
	} catch {
		return null;
	}
}

export type WriteVersionedInput<T> = {
	key: string;
	data: T;
};

export function writeVersioned<T>({ key, data }: WriteVersionedInput<T>): void {
	try {
		localStorage.setItem(key, encodeVersioned({ data }));
	} catch {
		// Quota or private-mode failures only lose persistence, never the in-memory session.
	}
}

export function removeVersioned({ key }: { key: string }): void {
	try {
		localStorage.removeItem(key);
	} catch {
		// Nothing to recover from: the entry is already unreachable.
	}
}

// ── Shape guards for the payloads this app persists ────────────────────────

export const isBooleanValue = (value: unknown): value is boolean => typeof value === 'boolean';

export const isStringValue = (value: unknown): value is string => typeof value === 'string';

export const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export type OneOfInput<T extends string> = {
	allowed: readonly T[];
};

/** Guard for a string union persisted as a plain string, e.g. a theme or a view mode. */
export const isOneOf =
	<T extends string>({ allowed }: OneOfInput<T>) =>
	(value: unknown): value is T =>
		typeof value === 'string' && (allowed as readonly string[]).includes(value);
